"use client";

import type { LeadFormExitMethod } from "@homeinstead/analytics";
import type { ReactNode } from "react";
import type { LeadFlowBlockPresentation, LeadFlowStepTheme } from "../types";

import {
  getThemeContrast,
  UNPAINTED_SURFACE,
} from "@homeinstead/tailwind-config/section-surfaces";
import { cn } from "@homeinstead/ui";
import {
  Dialog,
  DialogBody,
  DialogCloseButton,
  DialogContent,
} from "@homeinstead/ui/components/Dialog";
import { STEP_HEADING_SELECTOR } from "../constants/step-heading-selector";

// A bottom sheet on small viewports and a side sheet docked to the right edge
// on large ones. The breakpoint is a renderer constant.
const SHEET_CONTENT_CLASS_NAME =
  "fixed inset-x-0 bottom-0 h-auto max-h-[85dvh] w-full max-w-none rounded-b-none rounded-t-3xl lg:inset-y-0 lg:right-0 lg:left-auto lg:h-dvh lg:max-h-none lg:w-[32rem] lg:rounded-l-3xl lg:rounded-tr-none";

type SurfaceProps = {
  children: ReactNode;
  /**
   * The flow's `<form>`. The dialog portals into it, since a body portal
   * would detach the steps' submit button from the form.
   */
  container: HTMLFormElement;
  onClose: (exitMethod: LeadFormExitMethod) => void;
  surface: Exclude<LeadFlowBlockPresentation["surface"], "inline">;
  theme: LeadFlowStepTheme;
  title: string;
};

/**
 * The dialog paints the step's theme, whose painted values name section
 * surfaces. A dialog sits outside the section, so an `inherit` step sits on
 * the Page surface.
 */
function getDialogSurface(theme: LeadFlowStepTheme): string {
  return theme === "inherit" ? UNPAINTED_SURFACE.value : theme;
}

/**
 * The fullscreen or sheet surface that a flow's steps open on. It is
 * always open while rendered: closing hands control back to the flow, which
 * returns to its first step.
 */
function Surface({
  children,
  container,
  onClose,
  surface,
  theme,
  title,
}: SurfaceProps) {
  const isSheet = surface === "sheet";

  return (
    <Dialog open>
      <DialogContent
        aria-describedby={undefined}
        aria-label={title}
        className={cn(
          "bg-surface text-surface-foreground",
          isSheet && SHEET_CONTENT_CLASS_NAME,
        )}
        container={container}
        data-lead-flow-surface={surface}
        data-surface={getDialogSurface(theme)}
        fullscreen={!isSheet}
        // The flow moves focus to the step heading on step changes, so the
        // dialog does the same when it opens and leaves focus alone on close.
        onCloseAutoFocus={(event) => event.preventDefault()}
        onEscapeKeyDown={() => onClose("escape")}
        onOpenAutoFocus={(event) => {
          event.preventDefault();
          const panel = event.currentTarget as HTMLElement;
          const heading = panel.querySelector<HTMLElement>(
            STEP_HEADING_SELECTOR,
          );
          (heading ?? panel).focus({ preventScroll: true });
        }}
        onPointerDownOutside={
          isSheet ? () => onClose("outside_click") : undefined
        }
        width={isSheet ? "full" : "md"}
      >
        <div className="flex shrink-0 justify-end p-4">
          <DialogCloseButton
            className={cn(
              "static inset-auto text-surface-foreground focus-visible:outline-surface-foreground",
              {
                "hover:bg-white/10":
                  getThemeContrast(getDialogSurface(theme)) === "light",
              },
            )}
            onClick={() => onClose("x_button")}
            variant="ghost"
          />
        </div>
        <DialogBody>
          <div
            className={cn(
              "mx-auto w-full pb-[max(2.5rem,env(safe-area-inset-bottom))]",
              {
                "max-w-screen-lg sm:px-10": !isSheet,
              },
            )}
          >
            {children}
          </div>
        </DialogBody>
      </DialogContent>
    </Dialog>
  );
}

export { Surface };
